import { Link } from "react-router-dom";

const painPoints = [
  "Consultants often have not studied at the schools they advise you on.",
  "Parents and teachers rarely see how admissions work right now.",
  "Cold outreach usually gets brief or generic replies.",
  "Anonymous internet advice is contradictory and often outdated.",
  "Rankings and brochures leave out the lived reality.",
];

const pathways = [
  {
    title: "Admissions & Applications",
    items: [
      "Essay direction and brainstorming",
      "College list strategy",
      "Profile building and extracurricular choices",
      "Major selection and trade-offs",
      "Parent concerns and expectations",
    ],
  },
  {
    title: "Campus Life",
    items: [
      "Dorms, food, and social culture",
      "Clubs, internships, and opportunities",
      "What first-year students get wrong",
      "Managing pressure and balance",
    ],
  },
  {
    title: "Majors & Careers",
    items: [
      "What different majors actually lead to",
      "Academic workload and grading reality",
      "Internships and career outcomes",
      "Switching majors or double majors",
      "Long-term decisions explained honestly",
    ],
  },
  {
    title: "Exams & Logistics",
    items: [
      "SAT / IELTS / TOEFL strategy",
      "Scholarships and timelines",
      "Visas, housing, cost of living",
      "What no brochure tells you",
    ],
  },
];

const pricingTiers = [
  {
    name: "Quick Clarity",
    price: "599",
    strike: "1,299",
    duration: "20 minutes",
    points: [
      "Best for fast validation questions",
      "Major or campus-life clarity",
      "Early Decision or shortlist doubts",
      "A low-friction first conversation",
    ],
  },
  {
    name: "Focused Direction",
    price: "1,199",
    strike: "2,599",
    duration: "40 minutes",
    featured: true,
    points: [
      "Essay brainstorming and positioning",
      "Shortlist feedback from someone inside",
      "Major and career alignment discussion",
      "The strongest choice for most students",
    ],
  },
  {
    name: "Full Strategy",
    price: "1,799",
    strike: "3,899",
    duration: "60 minutes",
    points: [
      "Full profile review",
      "Deeper admissions strategy",
      "Major and career trade-off discussion",
      "Works well for parent + student calls",
    ],
  },
];

const logos = [
  { src: "/logos/stanford.png", alt: "Stanford" },
  { src: "/logos/mit.png", alt: "MIT" },
  { src: "/logos/harvard.png", alt: "Harvard" },
  { src: "/logos/upenn.png", alt: "UPenn" },
  { src: "/logos/cmu.png", alt: "Carnegie Mellon" },
  { src: "/logos/cornell.png", alt: "Cornell" },
  { src: "/logos/purdue.png", alt: "Purdue" },
  { src: "/logos/gtech.png", alt: "Georgia Tech" },
  { src: "/logos/uchicago.png", alt: "University of Chicago" },
  { src: "/logos/nyu.jpg", alt: "NYU" },
  { src: "/logos/nw.png", alt: "Northwestern" },
  { src: "/logos/emory.png", alt: "Emory" },
  { src: "/logos/oxford.png", alt: "Oxford" },
  { src: "/logos/cambridge.png", alt: "Cambridge" },
  { src: "/logos/lse.png", alt: "LSE" },
  { src: "/logos/ucl.png", alt: "UCL" },
];

const mentorBenefits = [
  "20-45 minute conversations",
  "Flexible schedule",
  "Structured platform and clear requests",
];

function SectionLabel({ children }) {
  return (
    <span className="inline-flex rounded-full border border-[#cfd5ee] bg-white/70 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-[#282E6A] shadow-sm">
      {children}
    </span>
  );
}

export default function LandingPage() {
  return (
    <div className="overflow-hidden bg-transparent text-gray-900">
      <section className="relative">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 pb-20 pt-16 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:px-8 lg:pb-24 lg:pt-24">
          <div className="relative z-10">
            <SectionLabel>Real Advice, Not Guesswork</SectionLabel>

            <h1 className="mt-6 max-w-4xl text-5xl leading-[0.96] text-[#111425] sm:text-6xl lg:text-7xl">
              Before you choose a university, talk to someone already living that decision.
            </h1>

            <p className="mt-6 max-w-2xl text-lg leading-8 text-[#5d647f] sm:text-xl">
              Hindsight connects high school students with current students and recent alumni from top universities, so decisions come from lived experience instead of marketing language.
            </p>

            <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
              <Link
                to="/register"
                className="inline-flex items-center justify-center rounded-xl bg-[#282E6A] px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.16em] text-white transition hover:bg-[#1e2458]"
              >
                Start with clarity
              </Link>
              <Link
                to="/register"
                className="inline-flex items-center justify-center rounded-xl border border-[#cfd5ee] bg-white/80 px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.16em] text-[#282E6A] transition hover:bg-[#eef0fa]"
              >
                Become a mentor
              </Link>
            </div>

            <div className="mt-10 grid max-w-2xl grid-cols-1 gap-4 sm:grid-cols-3">
              {[
                { label: "1-on-1 sessions", value: "Direct access" },
                { label: "Early pricing", value: "Starts at Rs 599" },
                { label: "Real mentors", value: "Current students + alumni" },
              ].map((stat) => (
                <div key={stat.label} className="rounded-2xl border border-white/70 bg-white/75 p-4 shadow-[0_18px_50px_-35px_rgba(22,28,59,0.45)] backdrop-blur-sm">
                  <p className="text-sm font-semibold text-[#111425]">{stat.value}</p>
                  <p className="mt-1 text-sm text-[#5d647f]">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="absolute inset-x-8 -top-10 h-40 rounded-full bg-[#e8d4a2]/45 blur-3xl" />
            <div className="relative grid gap-5 md:grid-cols-[0.82fr_1fr] lg:grid-cols-[0.72fr_1fr]">
              <div className="order-2 flex flex-col gap-5 md:order-1 md:pt-16">
                <div className="overflow-hidden rounded-[28px] border border-white/80 bg-white p-3 shadow-[0_24px_60px_-34px_rgba(23,30,67,0.45)]">
                  <img
                    src="/images/hero-mentorship1.jpg"
                    alt="Student on a mentorship video call"
                    className="h-72 w-full rounded-[20px] object-cover"
                  />
                </div>
                <div className="rounded-[24px] border border-[#d9def3] bg-[#eef0fa] p-5 text-[#282E6A] shadow-[0_20px_50px_-35px_rgba(40,46,106,0.5)]">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em]">Why this works</p>
                  <p className="mt-3 text-lg font-semibold leading-7 text-[#111425]">
                    One honest conversation can save months of confusion, bad assumptions, and expensive consulting.
                  </p>
                </div>
              </div>

              <div className="order-1 flex flex-col gap-5 md:order-2">
                <div className="rounded-[30px] border border-white/80 bg-[#111425] p-4 text-white shadow-[0_30px_80px_-35px_rgba(17,20,37,0.75)]">
                  <img
                    src="/images/hero-mentorship.jpg"
                    alt="Student video calling a mentor"
                    className="h-96 w-full rounded-[22px] object-cover"
                  />
                  <div className="grid gap-3 px-2 pb-2 pt-5 sm:grid-cols-2">
                    <div>
                      <p className="text-xs uppercase tracking-[0.18em] text-white/60">Students ask</p>
                      <p className="mt-2 text-sm leading-6 text-white/85">
                        What is campus culture really like? Is this major worth it? What would you do differently?
                      </p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-[0.18em] text-white/60">Mentors answer</p>
                      <p className="mt-2 text-sm leading-6 text-white/85">
                        With details you rarely get from rankings, brochures, or short cold messages.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="rounded-[22px] border border-white/80 bg-white p-5 shadow-[0_20px_50px_-35px_rgba(22,28,59,0.45)]">
                    <p className="text-xs uppercase tracking-[0.18em] text-[#5d647f]">No lock-ins</p>
                    <p className="mt-2 text-base font-semibold text-[#111425]">Single sessions, no large packages.</p>
                  </div>
                  <div className="rounded-[22px] border border-[#d9def3] bg-[#f6f3ea] p-5 shadow-[0_20px_50px_-35px_rgba(22,28,59,0.35)]">
                    <p className="text-xs uppercase tracking-[0.18em] text-[#5d647f]">Real clarity</p>
                    <p className="mt-2 text-base font-semibold text-[#111425]">Advice grounded in current reality.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-[#e0e4f2] bg-white/75 backdrop-blur-sm">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 py-20 lg:grid-cols-[0.9fr_1.1fr] lg:px-8">
          <div>
            <SectionLabel>Why Hindsight Exists</SectionLabel>
            <h2 className="mt-5 max-w-xl text-4xl leading-tight text-[#111425]">
              Choosing a university should not feel like assembling a future from second-hand noise.
            </h2>
            <p className="mt-5 max-w-lg text-base leading-7 text-[#5d647f]">
              Most students are forced to rely on partial information. Hindsight exists to replace vague reassurance with direct, current, specific perspective.
            </p>
            <div className="mt-8 rounded-[24px] border border-[#d9def3] bg-[#eef0fa] p-6">
              <p className="text-sm font-semibold text-[#111425]">What students usually need</p>
              <p className="mt-2 text-sm leading-7 text-[#5d647f]">
                A real person who can explain the academics, campus culture, admissions reality, career trade-offs, and what they wish they had known earlier.
              </p>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {painPoints.map((item, index) => (
              <div
                key={item}
                className={`rounded-[24px] border p-6 shadow-[0_18px_50px_-38px_rgba(23,30,67,0.45)] ${
                  index % 2 === 0
                    ? "border-[#e1e5f3] bg-white"
                    : "border-[#e8dcc0] bg-[#f7f2e6]"
                }`}
              >
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#5d647f]">
                  0{index + 1}
                </p>
                <p className="mt-4 text-base leading-7 text-[#111425]">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20 lg:px-8">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <SectionLabel>How It Works</SectionLabel>
            <h2 className="mt-5 text-4xl text-[#111425]">A simple flow designed to lower uncertainty fast.</h2>
          </div>
          <p className="max-w-xl text-base leading-7 text-[#5d647f]">
            No long-term commitments. No opaque consulting package. Just direct access to people who have already walked the path you are evaluating.
          </p>
        </div>

        <div className="mt-12 grid gap-6 lg:grid-cols-3">
          {[
            {
              image: "/images/student-group.JPG",
              step: "01",
              title: "Choose a mentor whose reality matches your ambition.",
              body: "Browse mentors by university, major, and background to find someone relevant to your exact decisions.",
            },
            {
              image: "/images/book-session.jpg",
              step: "02",
              title: "Book a session at the depth you need.",
              body: "Start small for fast clarity, or choose longer sessions when you want strategy and context.",
            },
            {
              image: "/images/clear-plan.jpg",
              step: "03",
              title: "Leave with direction instead of internet noise.",
              body: "Ask the questions you cannot ask a brochure, and get answers rooted in lived experience.",
            },
          ].map((card) => (
            <article key={card.step} className="overflow-hidden rounded-[28px] border border-white/80 bg-white shadow-[0_26px_70px_-42px_rgba(23,30,67,0.45)]">
              <img src={card.image} alt={card.title} className="h-52 w-full object-cover" />
              <div className="p-6">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#5d647f]">{card.step}</p>
                <h3 className="mt-3 text-2xl leading-tight text-[#111425]">{card.title}</h3>
                <p className="mt-4 text-sm leading-7 text-[#5d647f]">{card.body}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="border-y border-[#e0e4f2] bg-[#111425] text-white">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-8">
          <div className="grid gap-10 xl:grid-cols-[0.84fr_1.16fr] xl:items-stretch">
            <div className="grid gap-8 xl:grid-rows-[auto_1fr]">
              <div className="justify-self-start">
                <SectionLabel>What You Can Ask</SectionLabel>
              </div>
              <div>
                <h2 className="mt-5 max-w-xl text-4xl leading-tight text-white">
                  This is for the questions that actually shape decisions.
                </h2>
                <p className="mt-5 max-w-lg text-base leading-7 text-white/70">
                  Real conversations across applications, campus life, majors, careers, logistics, and the awkward details people usually avoid discussing publicly.
                </p>
              </div>

              <div className="rounded-[26px] border border-[#3a4275] bg-[#1d2350] p-6 lg:h-full lg:p-7">
                <div className="flex h-full flex-col justify-between gap-6">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#cfd5ee]">Student feedback</p>
                      <p className="mt-3 text-sm text-white/68 lg:whitespace-nowrap">
                        The best sessions feel specific, honest, and clear.
                      </p>
                    </div>
                    <p className="text-xs uppercase tracking-[0.18em] text-white/45">
                      Real sessions. Real perspective.
                    </p>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <blockquote className="rounded-[20px] bg-white/8 p-5 text-sm leading-7 text-white/82">
                      “The mentor embodied who I want to become. 10/10 experience.”
                      <footer className="mt-4 text-xs uppercase tracking-[0.18em] text-white/50">Karan</footer>
                    </blockquote>
                    <blockquote className="rounded-[20px] bg-white/8 p-5 text-sm leading-7 text-white/82">
                      “Very real. Very transparent. I finally felt clear.”
                      <footer className="mt-4 text-xs uppercase tracking-[0.18em] text-white/50">Rajvir</footer>
                    </blockquote>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid gap-5 md:grid-cols-2">
              {pathways.map((group) => (
                <div key={group.title} className="rounded-[26px] border border-white/10 bg-white/6 p-6 backdrop-blur-sm">
                  <h3 className="text-2xl leading-tight text-white">{group.title}</h3>
                  <ul className="mt-5 space-y-3">
                    {group.items.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-sm leading-6 text-white/78">
                        <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#e8d4a2]" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20 lg:px-8">
        <div className="rounded-[32px] border border-[#e0e4f2] bg-white/80 p-8 shadow-[0_30px_90px_-55px_rgba(23,30,67,0.5)] backdrop-blur-sm sm:p-10">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <SectionLabel>Global Mentors</SectionLabel>
              <h2 className="mt-5 text-4xl text-[#111425]">Mentors from universities students already dream about.</h2>
            </div>
            <p className="max-w-xl text-base leading-7 text-[#5d647f]">
              Students can speak with mentors from institutions across the US and UK, with more universities continuously added.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-2 items-center justify-items-center gap-x-8 gap-y-10 sm:grid-cols-3 lg:grid-cols-6">
            {logos.map((logo) => (
              <img
                key={logo.alt}
                src={logo.src}
                alt={logo.alt}
                className="h-12 object-contain opacity-75 transition hover:opacity-100"
              />
            ))}
          </div>

          <p className="mt-8 text-center text-sm text-[#5d647f]">And many more universities worldwide.</p>
        </div>
      </section>

      <section className="border-y border-[#e0e4f2] bg-[#f5f1e7]">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-8">
          <div className="max-w-3xl">
            <SectionLabel>Pricing</SectionLabel>
            <h2 className="mt-5 text-4xl text-[#111425]">Direct access, priced like a conversation instead of a consulting contract.</h2>
            <p className="mt-5 text-base leading-7 text-[#5d647f]">
              Traditional consulting can cost Rs 1-2 lakhs and still not give students a genuine conversation with someone actually studying there. Hindsight starts at Rs 599.
            </p>
          </div>

          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {pricingTiers.map((tier) => (
              <article
                key={tier.name}
                className={`relative rounded-[28px] border p-8 shadow-[0_28px_70px_-48px_rgba(23,30,67,0.4)] ${
                  tier.featured
                    ? "border-[#282E6A] bg-white"
                    : "border-white/80 bg-white/70 backdrop-blur-sm"
                }`}
              >
                {tier.featured && (
                  <span className="absolute -top-3 left-6 rounded-full bg-[#282E6A] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-white">
                    Most popular
                  </span>
                )}
                <h3 className="text-2xl text-[#111425]">{tier.name}</h3>
                <div className="mt-5 flex items-end gap-3">
                  <p className="text-4xl font-semibold text-[#282E6A]">Rs {tier.price}</p>
                  <p className="pb-1 text-sm text-gray-400 line-through">Rs {tier.strike}</p>
                </div>
                <p className="mt-2 text-xs font-semibold uppercase tracking-[0.16em] text-orange-500">Early access pricing</p>
                <p className="mt-5 text-sm font-medium text-[#111425]">{tier.duration}</p>
                <ul className="mt-6 space-y-3">
                  {tier.points.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm leading-6 text-[#5d647f]">
                      <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#282E6A]" />
                      {item}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/register"
              className="inline-flex items-center justify-center rounded-xl bg-[#282E6A] px-8 py-3.5 text-sm font-semibold uppercase tracking-[0.16em] text-white transition hover:bg-[#1e2458]"
            >
              Book a session
            </Link>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20 lg:px-8">
        <div className="grid gap-8 rounded-[34px] bg-[#111425] px-8 py-10 text-white shadow-[0_32px_100px_-48px_rgba(17,20,37,0.75)] lg:grid-cols-[1fr_0.78fr] lg:px-10">
          <div>
            <SectionLabel>For Mentors</SectionLabel>
            <h2 className="mt-5 text-4xl leading-tight text-white">
              Are you a college student or recent admit who can help someone decide better?
            </h2>
            <p className="mt-5 max-w-xl text-base leading-7 text-white/72">
              Share what your university is actually like, help students avoid bad decisions, and get compensated for your time.
            </p>
            <ul className="mt-8 space-y-3">
              {mentorBenefits.map((item) => (
                <li key={item} className="flex items-center gap-3 text-sm text-white/82">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#e8d4a2]" />
                  {item}
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <Link
                to="/register"
                className="inline-flex items-center justify-center rounded-xl border border-white/20 bg-white/10 px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.16em] text-white transition hover:bg-white/16"
              >
                Become a mentor
              </Link>
            </div>
          </div>

          <div className="overflow-hidden rounded-[28px] border border-white/10 bg-white/5 p-3">
            <img
              src="/images/university-student.JPG"
              alt="College student at university"
              className="h-full min-h-[320px] w-full rounded-[22px] object-cover"
            />
          </div>
        </div>
      </section>

      <section className="bg-[#282E6A] text-white">
        <div className="mx-auto max-w-6xl px-6 py-20 text-center lg:px-8">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-white/70">Final thought</p>
          <h2 className="mt-5 text-4xl leading-tight text-white">
            One conversation can change the direction of an entire application season.
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-7 text-white/80">
            Start with clarity now, instead of trying to reverse-engineer your future from rankings, threads, and assumptions.
          </p>
          <div className="mt-8">
            <Link
              to="/register"
              className="inline-flex items-center justify-center rounded-xl bg-white px-8 py-3.5 text-sm font-semibold uppercase tracking-[0.16em] text-[#282E6A] transition hover:bg-[#f3f4fb]"
            >
              Get started
            </Link>
          </div>
        </div>
      </section>

      <footer className="bg-[#0c1023] text-gray-300">
        <div className="mx-auto grid max-w-7xl gap-10 px-6 py-16 lg:grid-cols-[1.1fr_0.9fr] lg:px-8">
          <div className="space-y-4 text-sm">
            <p className="text-lg font-semibold text-white">Hindsight Inc.</p>
            <p className="max-w-md leading-7 text-gray-400">
              Your future deserves more than guesswork. Start with clarity, and make decisions with the benefit of someone who has already lived them.
            </p>
            <p>
              Email:{" "}
              <a href="mailto:hindsightin@gmail.com" className="text-white transition hover:text-[#e8d4a2]">
                hindsightin@gmail.com
              </a>
            </p>
            <p>Phone: +91-7507121487 · +91-9545826292</p>
          </div>

          <div className="space-y-4 text-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">Social</p>
            <a
              href="https://www.instagram.com/hindsight.inc"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 text-[#f06b9e] transition hover:text-[#ff93b9]"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M7.75 2h8.5C19.44 2 22 4.56 22 7.75v8.5C22 19.44 19.44 22 16.25 22h-8.5C4.56 22 2 19.44 2 16.25v-8.5C2 4.56 4.56 2 7.75 2zm0 1.5C5.4 3.5 3.5 5.4 3.5 7.75v8.5c0 2.35 1.9 4.25 4.25 4.25h8.5c2.35 0 4.25-1.9 4.25-4.25v-8.5c0-2.35-1.9-4.25-4.25-4.25h-8.5zM12 7a5 5 0 110 10 5 5 0 010-10zm0 1.5a3.5 3.5 0 100 7 3.5 3.5 0 000-7zm5.75-.88a1.13 1.13 0 11-2.26 0 1.13 1.13 0 012.26 0z" />
              </svg>
              @hindsight.inc
            </a>
            <a
              href="https://www.linkedin.com/company/thehindsightinc"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 text-[#72b5ff] transition hover:text-[#98c9ff]"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M4.98 3.5a2.5 2.5 0 11-.01 5.01 2.5 2.5 0 01.01-5.01zM3 8.98h4v12H3v-12zM9.5 8.98h3.8v1.64h.05c.53-1 1.83-2.05 3.76-2.05 4.02 0 4.76 2.64 4.76 6.08v6.33h-4v-5.6c0-1.33-.03-3.04-1.86-3.04-1.86 0-2.14 1.45-2.14 2.95v5.69h-4v-12z" />
              </svg>
              thehindsightinc
            </a>
          </div>
        </div>

        <div className="border-t border-white/10 py-6 text-center text-sm text-gray-500">
          © {new Date().getFullYear()} Hindsight Inc. · Co-founded by Siddhika Agrawal & Dhairya Agrawal
        </div>
      </footer>
    </div>
  );
}
